import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../services/api.js';
import ConfirmModal from '../../components/ConfirmModal.jsx';

/**
 * Service Maintenance (8947 / 8948): the services a customer can book and the
 * jobs each one generates. One job is done by one tug, so a job that needs two
 * tugs becomes two jobs when the booking is made. A job either fixes its tug
 * count or leaves it to the booking (the tugs requested, else what the
 * allocation rules advise for the vessel).
 */

const EMPTY_JOB = { jobName: '', tugCount: '', offsetHours: '' };
const EMPTY = { id: null, code: '', name: '', description: '', active: true, jobs: [{ ...EMPTY_JOB }] };

const hoursFromMinutes = (m) => (m ? String(Math.round((Number(m) / 60) * 100) / 100) : '');

function jobsSummary(jobs) {
  return jobs
    .map((j) => `${j.jobName} × ${j.tugCount ? `${j.tugCount} tug${j.tugCount > 1 ? 's' : ''}` : 'tugs per vessel'}`)
    .join(' · ');
}

// 8983: the list is searched, filtered, sorted and paged on the page.
const PAGE_SIZE = 10;
const SORTS = {
  code: (s) => s.code.toLowerCase(),
  name: (s) => s.name.toLowerCase(),
  jobs: (s) => s.jobs.length,
  bookings: (s) => s.bookingCount ?? 0,
  status: (s) => (s.active ? 0 : 1),
};

function SortHeader({ field, label, sort, onSort, className = '' }) {
  const active = sort.field === field;
  const icon = !active ? 'fa-sort text-muted' : sort.dir === 'asc' ? 'fa-sort-up' : 'fa-sort-down';
  return (
    <th className={className} aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <button
        type="button"
        className="btn btn-link btn-sm p-0 text-reset text-decoration-none text-uppercase fw-semibold small"
        onClick={() => onSort(field)}
        data-testid={`sort-${field}`}
      >
        {label} <i className={`fas ${icon} ms-1`} aria-hidden="true" />
      </button>
    </th>
  );
}

export default function ServiceMaintenancePage() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [pageMsg, setPageMsg] = useState(null);

  const [form, setForm] = useState(null); // the service being added / edited
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [sort, setSort] = useState({ field: 'code', dir: 'asc' });
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = services.filter((s) => {
      if (status === 'active' && !s.active) return false;
      if (status === 'inactive' && s.active) return false;
      if (!q) return true;
      return [s.code, s.name, s.description, ...s.jobs.map((j) => j.jobName)]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(q));
    });
    const key = SORTS[sort.field];
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const x = key(a);
      const y = key(b);
      if (x < y) return -dir;
      if (x > y) return dir;
      return a.code.localeCompare(b.code);
    });
  }, [services, search, status, sort]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const pageRows = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const onSort = (field) => {
    setSort((s) => ({ field, dir: s.field === field && s.dir === 'asc' ? 'desc' : 'asc' }));
    setPage(1);
  };

  const load = useCallback(async () => {
    setServices((await api.listServices()) || []);
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setLoadError('');
      try {
        await load();
      } catch (err) {
        setLoadError(err.message || 'Failed to load services.');
      } finally {
        setLoading(false);
      }
    })();
  }, [load]);

  // Set a form value and clear its error (and the form-level one).
  const setField = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[key];
      delete next.form;
      return next;
    });
  };

  const setJob = (index, key, value) => {
    setForm((f) => ({ ...f, jobs: f.jobs.map((j, i) => (i === index ? { ...j, [key]: value } : j)) }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[`job-${index}-${key}`];
      delete next.jobs;
      delete next.form;
      return next;
    });
  };

  const addJob = () => setForm((f) => ({ ...f, jobs: [...f.jobs, { ...EMPTY_JOB }] }));
  const removeJob = (index) => {
    setForm((f) => ({ ...f, jobs: f.jobs.filter((_, i) => i !== index) }));
    setErrors({});
  };
  const moveJob = (index, delta) =>
    setForm((f) => {
      const jobs = [...f.jobs];
      const to = index + delta;
      if (to < 0 || to >= jobs.length) return f;
      [jobs[index], jobs[to]] = [jobs[to], jobs[index]];
      return { ...f, jobs };
    });

  const openNew = () => {
    setErrors({});
    setForm({ ...EMPTY, jobs: [{ ...EMPTY_JOB }] });
  };

  const openEdit = (s) => {
    setErrors({});
    setForm({
      id: s.id,
      code: s.code,
      name: s.name,
      description: s.description || '',
      active: s.active,
      jobs: s.jobs.map((j) => ({
        jobName: j.jobName,
        tugCount: j.tugCount ? String(j.tugCount) : '',
        offsetHours: hoursFromMinutes(j.offsetMinutes),
      })),
    });
  };

  const validate = () => {
    const next = {};
    if (!form.code.trim()) next.code = 'Service code is required';
    else if (!/^[A-Za-z0-9-_]+$/.test(form.code.trim())) next.code = 'Use letters, numbers, - or _ in the code';
    if (!form.name.trim()) next.name = 'Service name is required';
    if (form.jobs.length === 0) next.jobs = 'Add at least one job to the service';
    form.jobs.forEach((j, i) => {
      if (!j.jobName.trim()) next[`job-${i}-jobName`] = 'Name the job';
      if (j.tugCount !== '') {
        const n = Number(j.tugCount);
        if (!Number.isInteger(n) || n < 1 || n > 6) next[`job-${i}-tugCount`] = 'Tugs must be a whole number from 1 to 6';
      }
      if (j.offsetHours !== '') {
        const h = Number(j.offsetHours);
        if (Number.isNaN(h) || h < 0) next[`job-${i}-offsetHours`] = 'Enter 0 or more hours';
        else if (h > 720) next[`job-${i}-offsetHours`] = 'At most 720 hours (30 days)';
      }
    });
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const save = async (e) => {
    e.preventDefault();
    setPageMsg(null);
    if (!validate()) return;
    setSaving(true);
    const payload = {
      code: form.code.trim(),
      name: form.name.trim(),
      description: form.description.trim() || null,
      active: form.active,
      jobs: form.jobs.map((j) => ({
        jobName: j.jobName.trim(),
        tugCount: j.tugCount === '' ? null : Number(j.tugCount),
        offsetMinutes: j.offsetHours === '' ? 0 : Math.round(Number(j.offsetHours) * 60),
      })),
    };
    try {
      const saved = form.id ? await api.updateService(form.id, payload) : await api.createService(payload);
      setForm(null);
      setPageMsg({ type: 'success', text: `Service ${saved.name} ${form.id ? 'updated' : 'added'}.` });
      await load();
    } catch (err) {
      const fieldErrors = Object.fromEntries((err.details || []).map((d) => [String(d.path).split('.')[0], d.message]));
      setErrors({ ...fieldErrors, form: err.message || 'Failed to save the service.' });
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setDeleting(true);
    try {
      await api.deleteService(confirmDel.id);
      setPageMsg({ type: 'success', text: `Service ${confirmDel.name} deleted.` });
      setConfirmDel(null);
      await load();
    } catch (err) {
      setPageMsg({ type: 'danger', text: err.message || 'Failed to delete the service.' });
      setConfirmDel(null);
    } finally {
      setDeleting(false);
    }
  };

  const fieldClass = (key, base = 'form-control form-control-sm') => `${base}${errors[key] ? ' is-invalid' : ''}`;

  return (
    <div className="container-fluid py-4" data-testid="service-maintenance-page">
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex flex-wrap gap-2 justify-content-between align-items-center bg-white p-3 rounded shadow-sm">
            <div>
              <h3 className="text-primary mb-0">
                <i className="fas fa-concierge-bell me-2" aria-hidden="true" />
                Service Maintenance
              </h3>
              <p className="text-muted mb-0 small">
                The services customers book and the jobs each one generates. One job is one tug.
              </p>
            </div>
            <button type="button" className="btn btn-primary" onClick={openNew} data-testid="new-service">
              <i className="fas fa-plus me-1" aria-hidden="true" />
              New Service
            </button>
          </div>
        </div>
      </div>

      {loadError && <div className="alert alert-danger">{loadError}</div>}
      {pageMsg && (
        <div className={`alert alert-${pageMsg.type}`} role="alert" data-testid="service-message">
          {pageMsg.text}
        </div>
      )}

      <div className="card border-0 shadow-sm">
        <div className="card-header bg-white border-bottom d-flex flex-wrap gap-2 align-items-center py-3">
          <div className="input-group input-group-sm" style={{ maxWidth: 320 }}>
            <span className="input-group-text bg-white">
              <i className="fas fa-search text-muted" aria-hidden="true" />
            </span>
            <input
              type="search"
              className="form-control"
              placeholder="Search code, service or job"
              aria-label="Search services"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              data-testid="service-search"
            />
          </div>
          <select
            className="form-select form-select-sm w-auto"
            aria-label="Filter by status"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setPage(1);
            }}
            data-testid="service-status-filter"
          >
            <option value="all">All statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          <span className="ms-auto small text-muted" data-testid="service-count">
            {filtered.length} of {services.length} service{services.length === 1 ? '' : 's'}
          </span>
        </div>
        <div className="card-body p-0">
          {loading ? (
            <div className="d-flex justify-content-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading…</span>
              </div>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0" data-testid="services-table">
                <thead className="bg-light text-secondary small text-uppercase">
                  <tr>
                    <SortHeader field="code" label="Code" sort={sort} onSort={onSort} />
                    <SortHeader field="name" label="Service" sort={sort} onSort={onSort} />
                    <SortHeader field="jobs" label="Jobs generated" sort={sort} onSort={onSort} />
                    <SortHeader field="bookings" label="Bookings" sort={sort} onSort={onSort} className="text-center" />
                    <SortHeader field="status" label="Status" sort={sort} onSort={onSort} />
                    <th aria-label="actions" />
                  </tr>
                </thead>
                <tbody>
                  {services.length === 0 && (
                    <tr>
                      <td colSpan={6} className="text-center text-muted py-4">
                        No services yet. Add the first one.
                      </td>
                    </tr>
                  )}
                  {services.length > 0 && filtered.length === 0 && (
                    <tr>
                      <td colSpan={6} className="text-center text-muted py-4" data-testid="service-no-match">
                        No services match your search or filter.
                      </td>
                    </tr>
                  )}
                  {pageRows.map((s) => (
                    <tr key={s.id} data-testid="service-row" data-code={s.code}>
                      <td className="fw-bold">{s.code}</td>
                      <td>
                        {s.name}
                        {s.description && <small className="d-block text-muted">{s.description}</small>}
                      </td>
                      <td className="small" data-testid="service-jobs">
                        {jobsSummary(s.jobs)}
                      </td>
                      <td className="text-center">{s.bookingCount ?? 0}</td>
                      <td>
                        <span className={`badge ${s.active ? 'bg-success' : 'bg-secondary'}`}>
                          {s.active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="text-end text-nowrap">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary me-1"
                          onClick={() => openEdit(s)}
                          data-testid="edit-service"
                        >
                          <i className="fas fa-pen me-1" aria-hidden="true" />
                          Edit
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => setConfirmDel(s)}
                          data-testid="delete-service"
                        >
                          <i className="fas fa-trash me-1" aria-hidden="true" />
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        {!loading && filtered.length > PAGE_SIZE && (
          <div className="card-footer bg-white d-flex flex-wrap gap-2 justify-content-between align-items-center">
            <span className="small text-muted" data-testid="service-page-info">
              Showing {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, filtered.length)} of{' '}
              {filtered.length}
            </span>
            <nav aria-label="Service pages">
              <ul className="pagination pagination-sm mb-0" data-testid="service-pagination">
                <li className={`page-item${currentPage === 1 ? ' disabled' : ''}`}>
                  <button type="button" className="page-link" onClick={() => setPage(currentPage - 1)} aria-label="Previous page">
                    &laquo;
                  </button>
                </li>
                {Array.from({ length: pageCount }, (_, i) => i + 1).map((n) => (
                  <li key={n} className={`page-item${n === currentPage ? ' active' : ''}`}>
                    <button
                      type="button"
                      className="page-link"
                      onClick={() => setPage(n)}
                      aria-current={n === currentPage ? 'page' : undefined}
                    >
                      {n}
                    </button>
                  </li>
                ))}
                <li className={`page-item${currentPage === pageCount ? ' disabled' : ''}`}>
                  <button type="button" className="page-link" onClick={() => setPage(currentPage + 1)} aria-label="Next page">
                    &raquo;
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        )}
      </div>

      {form && (
        <>
          <div className="modal d-block" tabIndex={-1} role="dialog" data-testid="service-modal">
            <div className="modal-dialog modal-dialog-centered modal-lg">
              <div className="modal-content">
                <form onSubmit={save} noValidate>
                  <div className="modal-header">
                    <h5 className="modal-title">
                      <i className="fas fa-concierge-bell me-2 text-primary" aria-hidden="true" />
                      {form.id ? `Edit ${form.name || 'Service'}` : 'New Service'}
                    </h5>
                    <button type="button" className="btn-close" aria-label="Close" onClick={() => setForm(null)} />
                  </div>
                  <div className="modal-body">
                    {errors.form && (
                      <div className="alert alert-danger" role="alert" data-testid="service-error">
                        {errors.form}
                      </div>
                    )}
                    <div className="row g-2">
                      <div className="col-md-4 mb-2">
                        <label htmlFor="svc-code" className="form-label small fw-bold text-muted">
                          Code <span className="text-danger">*</span>
                        </label>
                        <input
                          id="svc-code"
                          className={fieldClass('code')}
                          maxLength={32}
                          value={form.code}
                          onChange={(e) => setField('code', e.target.value)}
                          data-testid="service-code"
                        />
                        {errors.code && <div className="invalid-feedback">{errors.code}</div>}
                      </div>
                      <div className="col-md-8 mb-2">
                        <label htmlFor="svc-name" className="form-label small fw-bold text-muted">
                          Service name <span className="text-danger">*</span>
                        </label>
                        <input
                          id="svc-name"
                          className={fieldClass('name')}
                          maxLength={120}
                          value={form.name}
                          onChange={(e) => setField('name', e.target.value)}
                          data-testid="service-name"
                        />
                        {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                      </div>
                    </div>
                    <div className="mb-2">
                      <label htmlFor="svc-desc" className="form-label small fw-bold text-muted">
                        Description
                      </label>
                      <textarea
                        id="svc-desc"
                        rows={2}
                        className="form-control form-control-sm"
                        maxLength={1000}
                        value={form.description}
                        onChange={(e) => setField('description', e.target.value)}
                        data-testid="service-description"
                      />
                    </div>
                    <div className="form-check form-switch mb-3">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="svc-active"
                        checked={form.active}
                        onChange={(e) => setField('active', e.target.checked)}
                        data-testid="service-active"
                      />
                      <label className="form-check-label small fw-bold text-muted" htmlFor="svc-active">
                        Active — customers can book it
                      </label>
                    </div>

                    <h6 className="text-secondary mb-1">Jobs this service generates</h6>
                    <p className="small text-muted mb-2">
                      Each job is done by one tug. Leave <strong>Tugs</strong> blank to use as many tugs as the booking
                      needs (the tugs requested, else the allocation rules for the vessel). <strong>Due after</strong> sets
                      the job&apos;s time from the booking&apos;s; the dispatcher can still change it.
                    </p>
                    {errors.jobs && <div className="alert alert-danger py-2 small">{errors.jobs}</div>}
                    <div data-testid="service-job-list">
                      {form.jobs.map((j, i) => (
                        <div key={i} className="row g-2 align-items-start mb-2" data-testid="service-job-row">
                          <div className="col-1 pt-1 text-muted small fw-bold">{i + 1}.</div>
                          <div className="col-5">
                            <input
                              aria-label={`Job ${i + 1} name`}
                              className={fieldClass(`job-${i}-jobName`)}
                              placeholder="Job name, e.g. Docking"
                              maxLength={120}
                              value={j.jobName}
                              onChange={(e) => setJob(i, 'jobName', e.target.value)}
                              data-testid="service-job-name"
                            />
                            {errors[`job-${i}-jobName`] && (
                              <div className="invalid-feedback">{errors[`job-${i}-jobName`]}</div>
                            )}
                          </div>
                          <div className="col-2">
                            <input
                              aria-label={`Job ${i + 1} tugs`}
                              type="number"
                              min="1"
                              max="6"
                              className={fieldClass(`job-${i}-tugCount`)}
                              placeholder="Tugs"
                              value={j.tugCount}
                              onChange={(e) => setJob(i, 'tugCount', e.target.value)}
                              data-testid="service-job-tugs"
                            />
                            {errors[`job-${i}-tugCount`] && (
                              <div className="invalid-feedback">{errors[`job-${i}-tugCount`]}</div>
                            )}
                          </div>
                          <div className="col-2">
                            <input
                              aria-label={`Job ${i + 1} due after (hours)`}
                              type="number"
                              min="0"
                              step="0.5"
                              className={fieldClass(`job-${i}-offsetHours`)}
                              placeholder="Due after (h)"
                              value={j.offsetHours}
                              onChange={(e) => setJob(i, 'offsetHours', e.target.value)}
                              data-testid="service-job-offset"
                            />
                            {errors[`job-${i}-offsetHours`] && (
                              <div className="invalid-feedback">{errors[`job-${i}-offsetHours`]}</div>
                            )}
                          </div>
                          <div className="col-2 text-end text-nowrap">
                            <button
                              type="button"
                              className="btn btn-sm btn-light"
                              onClick={() => moveJob(i, -1)}
                              disabled={i === 0}
                              aria-label={`Move job ${i + 1} up`}
                            >
                              <i className="fas fa-arrow-up" aria-hidden="true" />
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-light"
                              onClick={() => moveJob(i, 1)}
                              disabled={i === form.jobs.length - 1}
                              aria-label={`Move job ${i + 1} down`}
                            >
                              <i className="fas fa-arrow-down" aria-hidden="true" />
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-light text-danger"
                              onClick={() => removeJob(i)}
                              disabled={form.jobs.length === 1}
                              aria-label={`Remove job ${i + 1}`}
                              data-testid="remove-service-job"
                            >
                              <i className="fas fa-times" aria-hidden="true" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      onClick={addJob}
                      disabled={form.jobs.length >= 12}
                      data-testid="add-service-job"
                    >
                      <i className="fas fa-plus me-1" aria-hidden="true" />
                      Add Job
                    </button>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setForm(null)}>
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary btn-sm" disabled={saving} data-testid="save-service">
                      {saving && <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />}
                      Save Service
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </>
      )}

      <ConfirmModal
        open={Boolean(confirmDel)}
        title="Delete service"
        message={
          confirmDel && (
            <>
              Delete <strong>{confirmDel.name}</strong> and its job mapping? A service that has bookings cannot be
              deleted — mark it inactive instead.
            </>
          )
        }
        busy={deleting}
        onConfirm={remove}
        onCancel={() => setConfirmDel(null)}
      />
    </div>
  );
}
